import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { FolderInput, LoaderCircle, RotateCcw } from 'lucide-react'

import * as Mutation from '../../../api/gql/mutation'

const MODES = ['CONSOLIDATION', 'REVISION'] as const
type Mode = (typeof MODES)[number]

interface ReopenDialogProps {
  submittalId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  refetch: () => void
  /** The current revision was already published to Doc Control — those files stay there. */
  published: boolean
}

/** Freelo #33447936: an admin takes back a decided submittal, either to the consolidation or into a new revision. */
const ReopenDialog: React.FC<ReopenDialogProps> = ({ submittalId, open, onOpenChange, refetch, published }) => {
  const { t } = useTranslation()
  const [mode, setMode] = useState<Mode | ''>('')
  const [reason, setReason] = useState('')
  const [reopenSubmittal, { loading }] = useMutation(Mutation.REOPEN_SUBMITTAL)

  const handleClose = () => {
    if (!loading) onOpenChange(false)
  }

  const handleConfirm = async () => {
    if (!mode || !reason.trim()) return
    try {
      await reopenSubmittal({ variables: { id: submittalId, mode, reason: reason.trim() } })
      toastr.success('', t('Submittals_reopened'))
      refetch()
      onOpenChange(false)
    } catch (error: any) {
      toastr.error(t('Common_error'), error.message)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) handleClose() }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5 text-primary" />
            {t('Submittals_reopen_title')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{t('Submittals_reopen_mode_label')}</Label>
            <RadioGroup value={mode} onValueChange={(next) => setMode(next as Mode)}>
              {MODES.map((m) => (
                <div key={m} className="flex items-start gap-2.5 rounded px-1 py-1.5 hover:bg-muted">
                  <RadioGroupItem value={m} id={`reopen-mode-${m}`} className="mt-0.5" />
                  <Label htmlFor={`reopen-mode-${m}`} className="flex-1 cursor-pointer space-y-0.5 font-normal">
                    <span className="block font-medium">{t(`Submittals_reopen_mode_${m}`)}</span>
                    <span className="block text-xs text-muted-foreground">{t(`Submittals_reopen_mode_${m}_hint`)}</span>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="reopen-reason">{t('Submittals_reopen_reason_label')}</Label>
            <Textarea
              id="reopen-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('Submittals_reopen_reason_placeholder')}
              rows={3}
            />
          </div>

          {published && (
            <Alert>
              <FolderInput className="h-4 w-4" />
              <AlertDescription>{t('Submittals_reopen_published_note')}</AlertDescription>
            </Alert>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t pt-4">
          <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
            {t('Common_cancel')}
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={loading || !mode || !reason.trim()}>
            {loading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
            {t('Submittals_reopen_btn')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default ReopenDialog
